// DCF AI 채팅 패널 — 바닐라 dcf.html 채팅 마크업 + dcf.js 의
// sendMessage/handleEvent(SSE)/chatCtx/quickSend/appendTyping/scheduleDcfStream/
// applyDcfPatch 연동/toggleChat/채팅 리사이저 이식 (Phase 2 완성 B).
//
// - POST /agent/dcf/chat (FormData: messages/dcf_state/conversation_id) → SSE 스트리밍
//   은 lib/sse.ts 의 postSse 제너레이터 사용 (watchdog 내장).
// - token 이벤트는 requestAnimationFrame + 최소 120ms 배칭으로만 리렌더
//   (바닐라 scheduleDcfStream — 토큰마다 재파싱하면 freeze 재발).
// - 대화는 프로젝트별 localStorage 캐시: va_dcf_chat::<userKey>::<projectId>
//   (바닐라 chatKey 와 동일 — 바닐라 /dcf 와 양방향 공유).
// - 마크다운 경량 렌더는 lib/markdown.ts (원문 전체 esc 후 제한 마크업만 생성).
import { memo, useEffect, useRef, useState } from "react";
import { BrandMark } from "../../ui/BrandMark";
import { useT } from "../../lib/i18n";
import { hasMathDelimiters, renderMathIn } from "../../lib/katex";
import { mdToHtml } from "../../lib/markdown";
import { postSse } from "../../lib/sse";
import { toolLabel } from "../../lib/toolLabels";
import { useAuth } from "../../stores/auth";
import { useDcf } from "../../stores/dcf";
import type { DcfPayload } from "../../types/dcf";

interface ChatMsg {
  role: "user" | "assistant" | "note";
  content: string;
}

interface Props {
  /** 채팅 접기 버튼 → 워크스페이스가 chat-collapsed 클래스 토글 */
  onCollapse: () => void;
  /** 리사이저 드래그 → 워크스페이스 --chat-w 갱신 */
  onResize: (px: number) => void;
  /** dcf_patch 적용/재계산 완료 시 → 워크스페이스 갱신 플래시 */
  onWorkspaceRefresh: () => void;
  /** download artifact 수신 시 → Report 탭으로 전환 */
  onDownload: () => void;
}

const CHAT_BASE = "va_dcf_chat";
const CHAT_DEFAULT_W = 360;

function chatKey(userKey: string | null, pid: string): string {
  return `${CHAT_BASE}::${userKey || "_anon"}::${pid}`;
}

function loadChat(userKey: string | null, pid: string): ChatMsg[] {
  try {
    const arr = JSON.parse(localStorage.getItem(chatKey(userKey, pid)) || "[]") as ChatMsg[];
    return Array.isArray(arr)
      ? arr.filter((m) => m && (m.role === "user" || m.role === "assistant"))
      : [];
  } catch {
    return [];
  }
}

// 바닐라 TYPING_PHRASES — 4초 단위로 다음 문구로 진행
const TYPING_PHRASES = [
  "평가 데이터 정리 중",
  "가정·추정 검토 중",
  "재무지표 분석 중",
  "코멘트 작성 중",
  "마무리 중",
];

// 바닐라 quick-btns — 라벨은 i18n(chip.*), 프롬프트는 한국어 유지(에이전트 입력)
const QUICK_CHIPS: Array<{ key: string; prompt: string }> = [
  { key: "chip.dcf_explain", prompt: "이번 DCF 평가의 방법론(FCFF·WACC·Terminal Value)과 결과를 단계적으로 설명해줘." },
  { key: "chip.review", prompt: "이번 평가의 주요 리스크와 검토 포인트 3가지를 간결하게 짚어줘." },
  { key: "chip.review_ready", prompt: "이 DCF의 리뷰 준비도를 진단해줘. 리뷰어(감사인)가 관심 가질 부분과 미리 보강할 설명·증빙을 알려줘." },
  { key: "chip.revenue", prompt: "매출 시나리오(매출 성장 가정)가 적절한지 검토하고 의견을 줘." },
  { key: "chip.cogs", prompt: "매출원가율 가정이 합리적인지 검토해줘." },
  { key: "chip.sga", prompt: "판관비율 가정에 대해 검토 의견을 줘." },
  { key: "chip.capex", prompt: "CAPEX 가정이 감가상각·성장 전망과 정합적인지 검토해줘." },
  { key: "chip.macro", prompt: "물가상승률 등 거시경제 가정이 이번 평가에 미치는 영향을 설명해줘." },
  { key: "chip.wacc_tv", prompt: "WACC와 영구성장률 가정이 적절한지, Terminal Value 비중 관점에서 검토해줘." },
];

/** dcf_patch summary → 채팅 patch-note 문구 (바닐라는 셀 flash — React 는 노트로 요약 표시) */
function patchNoteText(summary: unknown): string {
  const entries =
    summary && typeof summary === "object" && !Array.isArray(summary)
      ? Object.entries(summary as Record<string, unknown>)
      : [];
  if (!entries.length) return "에이전트가 DCF 입력값을 수정했습니다.";
  const fmtVal = (v: unknown): string => {
    if (typeof v === "number") return v.toLocaleString("ko-KR");
    if (v && typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return (
    "입력값 갱신: " + entries.map(([k, v]) => `${k} → ${fmtVal(v)}`).join(", ")
  );
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function DsMark({ loading }: { loading?: boolean }) {
  return <BrandMark className="ds-mark" loading={loading} />;
}

/** 에이전트 말풍선 — RawHtml memo 패턴(html 동일하면 재렌더 없음) + 렌더 후
 *  KaTeX auto-render 적용 (바닐라 appendMsg → renderMath). memo 덕분에 KaTeX 가
 *  주입한 DOM 을 React 가 다시 덮어쓰지 않는다. katex 청크는 수식 구분자가
 *  실제로 있을 때만 dynamic import 된다 (echarts 와 같은 코드 스플리팅). */
const AgentBubble = memo(function AgentBubble({ content }: { content: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMathDelimiters(content)) return;
    // auto-render 는 el 의 텍스트 노드를 직접 변형한다 — 언마운트 뒤 도착해도
    // 이미 떨어져 나간 노드를 만질 뿐이라 정리(cleanup)가 필요 없다.
    void renderMathIn(el);
  }, [content]);
  return (
    <div
      ref={ref}
      className="msg-bubble agent"
      dangerouslySetInnerHTML={{ __html: mdToHtml(content) }}
    />
  );
});

export default function ChatPanel({
  onCollapse,
  onResize,
  onWorkspaceRefresh,
  onDownload,
}: Props) {
  const { t } = useT();
  const current = useDcf((s) => s.current);
  const userKey = useDcf((s) => s.userKey);
  const projectId = current?.project.id || "dcf-default";
  const projectName = current?.project.name || "";

  // 프로젝트 열 때의 결과 유무 — 인트로 문구 분기 (바닐라 openProject intro)
  const [hadResultAtOpen] = useState(() => !!current?.result);
  // 빈 양식으로 방금 생성됨 — 바닐라 openProject(autocalc===false) 인트로 분기
  const [blankAtOpen] = useState(
    () => useDcf.getState().blankIntroId === current?.project.id,
  );
  const [msgs, setMsgs] = useState<ChatMsg[]>(() => loadChat(userKey, projectId));
  const msgsRef = useRef<ChatMsg[]>(msgs);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  /** null = 스트리밍 아님, string = 라이브 렌더 중인 누적 텍스트 */
  const [streamText, setStreamText] = useState<string | null>(null);

  // token 배칭 (바닐라 scheduleDcfStream: rAF + 최소 120ms)
  const bufRef = useRef("");
  const streamingRef = useRef(false);
  const rafRef = useRef(0);
  const lastFlushRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // 진행 타이머 — 바닐라 appendTyping 의 1초 인터벌
  useEffect(() => {
    if (!sending) return;
    setElapsed(0);
    const start = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [sending]);

  // 새 메시지/스트림 갱신 시 맨 아래로
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, streamText, sending]);

  const persist = (arr: ChatMsg[]) => {
    msgsRef.current = arr;
    setMsgs(arr);
    try {
      localStorage.setItem(
        chatKey(userKey, projectId),
        JSON.stringify(arr.filter((m) => m.role !== "note").slice(-40)),
      );
    } catch {
      /* quota 등 — 캐시 실패는 무시 */
    }
  };

  const flushStream = () => {
    lastFlushRef.current = performance.now();
    if (aliveRef.current) setStreamText(bufRef.current);
  };
  const scheduleFlush = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (performance.now() - lastFlushRef.current < 120) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = 0;
          flushStream();
        });
        return;
      }
      flushStream();
    });
  };

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || sending) return;
    setInput("");
    const base: ChatMsg[] = [...msgsRef.current, { role: "user", content: text }];
    persist(base);
    setSending(true);
    setStatusLabel(null);
    setStreamText(null);
    bufRef.current = "";
    streamingRef.current = false;

    // FormData: messages + 현재 payload/result (바닐라 sendMessage)
    const cur = useDcf.getState().current;
    const fd = new FormData();
    fd.append(
      "messages",
      JSON.stringify(base.filter((m) => m.role !== "note")),
    );
    // 바닐라: 사업내용은 항상 최신 입력을 business_text 로 덧붙임 (동종업계 비교 등 즉시 활용)
    const payloadForChat: DcfPayload = { ...(cur?.payload || {}) };
    const liveBiz = String(
      payloadForChat.business_description || payloadForChat.business_text || "",
    ).trim();
    if (liveBiz) payloadForChat.business_text = liveBiz;
    fd.append(
      "dcf_state",
      JSON.stringify({ payload: payloadForChat, result: cur?.result || null }),
    );
    fd.append("conversation_id", cur?.project.id || "dcf-default");

    let agentText = "";
    let recalcRequested = false;
    try {
      for await (const ev of postSse("/agent/dcf/chat", fd)) {
        switch (ev.event) {
          case "status":
            if (!streamingRef.current)
              setStatusLabel(String(ev["message"] || "") || null);
            break;
          case "tool_start":
            if (!streamingRef.current)
              setStatusLabel(`${toolLabel(String(ev["tool"] || ""))} 실행 중`);
            break;
          case "tool_end":
            break;
          case "token":
            streamingRef.current = true;
            bufRef.current += String(ev["text"] || "");
            scheduleFlush();
            break;
          case "artifact": {
            const a = (ev["artifact"] || {}) as Record<string, unknown>;
            if (a.type === "dcf_patch") {
              useDcf
                .getState()
                .applyPatch((a.patch || {}) as Record<string, unknown>);
              persist([
                ...msgsRef.current,
                { role: "note", content: patchNoteText(a.summary) },
              ]);
              onWorkspaceRefresh();
            } else if (a.type === "dcf_recalc") {
              recalcRequested = true;
            } else if (a.type === "download" && typeof a.url === "string") {
              useDcf.getState().addFileLink({
                id: Date.now(),
                kind: "ok",
                label: String(a.label || a.filename || "다운로드"),
                url: a.url,
              });
              onDownload(); // Report 탭으로 전환해 링크를 보여준다 (바닐라 동일)
            }
            break;
          }
          case "reply":
            if (ev["interim"]) {
              if (!streamingRef.current && ev["text"])
                setStatusLabel(String(ev["text"]).slice(0, 80) + "…");
            } else {
              agentText = String(ev["text"] || "");
            }
            break;
          case "error":
            agentText = "⚠ " + String(ev["message"] || "오류");
            break;
          case "done":
            break;
        }
      }
    } catch (e) {
      if (errMsg(e) === "unauthorized") {
        // 401 → 인증 재검증(로그인 오버레이). 메시지는 남기지 않음 (바닐라 gotoLogin)
        void useAuth.getState().checkAuth();
      } else {
        agentText = agentText || "오류: " + errMsg(e);
      }
    }

    // 마무리 — 스트림 잔여분 flush 취소 후 최종 말풍선 확정
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const streamed = bufRef.current;
    bufRef.current = "";
    streamingRef.current = false;
    if (!aliveRef.current) return;
    setStreamText(null);
    setStatusLabel(null);
    // 바닐라는 reply(final) 만 확정하지만, 유실 대비로 스트림 누적분을 폴백으로 쓴다
    const finalText = agentText || streamed;
    if (finalText) {
      persist([...msgsRef.current, { role: "assistant", content: finalText }]);
    }
    setSending(false);
    if (recalcRequested) {
      const ok = await useDcf.getState().recalc();
      if (ok) onWorkspaceRefresh();
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return; // 한글 조합 중 Enter 무시 (바닐라 handleKey)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  // ── 리사이저 (바닐라 chatResizer — 드래그 너비 조절, 더블클릭 기본값) ──
  const startDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const ws = (e.currentTarget as HTMLElement).closest(".workspace");
    const left = ws ? ws.getBoundingClientRect().left : 0;
    document.body.classList.add("chat-resizing");
    const onMove = (me: MouseEvent) => onResize(me.clientX - left);
    const onUp = () => {
      document.body.classList.remove("chat-resizing");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // 진행 라벨 — 상태 메시지 우선, 없으면 4초 단위 문구 진행 (바닐라 appendTyping)
  const phrase =
    TYPING_PHRASES[Math.min(TYPING_PHRASES.length - 1, Math.floor(elapsed / 4))];
  const typingLabel = `${statusLabel || phrase}… ${elapsed}초`;

  return (
    <div className="chat-panel">
      <div
        className="chat-resizer"
        title={t("chat.resizer")}
        onMouseDown={startDrag}
        onDoubleClick={() => onResize(CHAT_DEFAULT_W)}
      >
        <span className="chat-resizer-grip" />
      </div>
      <div className="chat-header">
        <div className="chat-agent-mark" aria-hidden="true">
          <DsMark />
        </div>
        <div className="chat-agent-copy">
          <h3>DCF Agent</h3>
          <span>{t("lbl.online")}</span>
        </div>
        <button
          className="chat-collapse"
          type="button"
          title={t("chat.collapse")}
          aria-label={t("chat.collapse")}
          onClick={onCollapse}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 3v18" />
            <path d="m16 15-3-3 3-3" />
          </svg>
        </button>
      </div>
      <div className="chat-messages" ref={scrollRef}>
        {/* 인트로 (바닐라 openProject — 결과 유무별 문구, chatCtx 에는 미포함) */}
        <div className="msg-row">
          <div className="msg-avatar agent">
            <DsMark />
          </div>
          <div className="msg-bubble agent">
            {blankAtOpen ? (
              // 바닐라 openProject(autocalc===false) 문구와 동일
              <>
                <b>{projectName}</b> 빈 양식 프로젝트입니다.
                <br />
                <br />
                Assumptions·Projection 탭에서 매출·비용·CAPEX 등을 입력한 뒤{" "}
                <b>재계산</b>을 눌러주세요.
              </>
            ) : (
              <>
                <b>{projectName}</b> DCF 프로젝트를 열었습니다.
                <br />
                <br />
                {hadResultAtOpen ? (
                  <>
                    마지막 저장 시점의 계산 결과를 표시합니다. 가정을 수정한 뒤{" "}
                    <b>재계산</b>을 누르면 결과가 갱신됩니다.
                  </>
                ) : (
                  <>
                    가정·추정 드라이버를 확인하고 <b>재계산</b>을 누르면 DeepSearch
                    밸류에이션 엔진이 결과를 산출합니다.
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {msgs.map((m, i) =>
          m.role === "note" ? (
            <div key={i} className="msg-row">
              <div className="msg-avatar agent">
                <DsMark />
              </div>
              <div className="patch-note">{m.content}</div>
            </div>
          ) : m.role === "user" ? (
            <div key={i} className="msg-row user">
              <div className="msg-avatar user">You</div>
              <div className="msg-bubble user" style={{ whiteSpace: "pre-wrap" }}>
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="msg-row">
              <div className="msg-avatar agent">
                <DsMark />
              </div>
              <AgentBubble content={m.content} />
            </div>
          ),
        )}
        {streamText != null ? (
          // 라이브 스트리밍 — 최종 말풍선과 같은 클래스로 표·헤딩이 즉시 형식 잡힘
          <div className="msg-row">
            <div className="msg-avatar agent">
              <DsMark loading />
            </div>
            <div
              className="msg-bubble agent"
              dangerouslySetInnerHTML={{ __html: mdToHtml(streamText) }}
            />
          </div>
        ) : sending ? (
          <div className="msg-row">
            <div className="msg-avatar agent">
              <DsMark loading />
            </div>
            <div className="msg-typing">
              <span className="typing-label">{typingLabel}</span>
            </div>
          </div>
        ) : null}
      </div>
      <div className="quick-btns">
        {QUICK_CHIPS.map((c) => (
          <button
            key={c.key}
            className="quick-btn"
            disabled={sending}
            onClick={() => void send(c.prompt)}
          >
            {t(c.key)}
          </button>
        ))}
      </div>
      <div className="chat-input-row">
        <textarea
          className="chat-input"
          rows={1}
          placeholder={t("chat.placeholder")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
        />
        <button
          className="send-btn"
          aria-label={t("chat.send")}
          title={t("chat.send")}
          disabled={sending}
          onClick={() => void send(input)}
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 19V5" />
            <path d="m5 12 7-7 7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}
